import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import ShareBar from "@/components/feature/ShareBar";
import { news } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const categoryStyle: Record<string, string> = {
  资讯: "bg-primary-100 text-primary-800",
  影评: "bg-accent-100 text-accent-900",
  行业动态: "bg-secondary-200 text-secondary-900",
};

export default function NewsDetail() {
  const { id } = useParams();
  const item = news.find((n) => n.id === id);

  useSeo({
    title: item ? `${item.title} - 91电影` : "资讯详情 - 91电影",
    description: item?.excerpt,
    keywords: item ? `${item.tags.join(",")},91电影` : "91电影,电影资讯",
  });

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-8">
        <EmptyState
          icon="ri-file-unknow-line"
          title="内容不存在或已被删除"
          description="你访问的资讯可能已经下架，去看看其他精彩内容吧。"
          action={
            <Link
              to="/category/news"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md px-5 py-2.5 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
          }
        />
      </div>
    );
  }

  const related = news
    .filter((n) => n.id !== item.id && n.category === item.category)
    .concat(news.filter((n) => n.id !== item.id && n.category !== item.category))
    .slice(0, 3);

  return (
    <article className="w-full px-4 md:px-8 py-8 animate-fade-up">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "电影资讯", to: "/category/news" },
          { label: item.category },
        ]}
      />

      <div className="mt-6 max-w-4xl">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`text-xs font-bold rounded-md px-2.5 py-1 ${categoryStyle[item.category] ?? categoryStyle["资讯"]}`}>
            {item.category}
          </span>
          {item.tags.map((tag) => (
            <span key={tag} className="text-xs text-foreground-500 bg-background-200 rounded px-2 py-1">
              #{tag}
            </span>
          ))}
        </div>

        <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 leading-snug mt-4">
          {item.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" />
            {item.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line" />
            {item.date}
          </span>
          <Link
            to="/category/news"
            className="flex items-center gap-1.5 text-primary-700 hover:text-primary-800 font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-folder-line" />
            查看栏目
          </Link>
        </div>
      </div>

      <div className="mt-7 w-full max-w-4xl rounded-lg overflow-hidden bg-secondary-200 aspect-[16/9]">
        <img
          src={item.cover}
          alt={`${item.title} 资讯配图`}
          title={`${item.title} 电影资讯 91电影`}
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="mt-8 max-w-4xl">
        <div className="flex flex-col gap-5">
          {item.content.map((para, idx) => (
            <p key={idx} className="text-base leading-[1.9] text-foreground-800">
              {para}
            </p>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-background-200">
          <ShareBar />
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            <h2 className="font-heading font-bold text-xl text-foreground-950">相关推荐</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((r) => (
              <NewsCard key={r.id} item={r} />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}